import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import './FilesSection.scss'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import FlatList from 'flatlist-react'
import { FileCard, SecondaryButton, UploadFilePopUp, useUpdateEffect } from 'ui_kit_qmd'
import filesIcon from '../../../../images/common/file.png'
import { ChartSection } from '../chart-section/ChartSection'
import { addMediaFile, deleteMediaFile, getMediaFiles, setMediaFileTags } from '../../../../store/charts/ChartsThunk'
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_EXTENSIONS_STR, SIZE_10_MB, styles, text } from './Constants'
import { getStateRules, parseUserDateTime } from '../../../../utils/common'
import plusIcon from '../../../../images/management/clinicMembers/plusIcon.png'
import { errorCodes, getUserFriendlyErrorMessage } from '../../../../utils/errormessages'
import { setChartsError } from '../../../../store/charts/ChartsActions'
import { apiCall } from '../../../../utils/restclient'
import { useMediaFiles } from './hooks/useMediaFiles'

export const FilesSection = forwardRef(
  // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
  ({ id, isMergedAccount = false, userTimeZone, setToastErrorMessage, patientInfo }, ref) => {
    const dispatch = useDispatch()
    const fileCardRef = useRef()

    // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
    const settingsPerState = useSelector((state) => state.management.statesSettings.SettingsPerState)
    // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
    const mediaSummary = useSelector((state) => state.chartsReducer?.patientInfo?.[id]?.MediaFileSummary)

    const [isCollapsed, setIsCollapsed] = useState(true)
    const [hasOpened, setHasOpened] = useState(false)

    const [uploadedFile, setUploadedFile] = useState({})
    const [showUploadPopUp, setShowUploadPopUp] = useState(false)
    const [patientDrugRules, setPatientDrugRules] = useState(
      getStateRules(patientInfo?.[id]?.Address?.StateCode?.toUpperCase(), settingsPerState),
    )

    const { filteredMediaFiles, getTagFilter, mediaFiles } = useMediaFiles({ id })

    useUpdateEffect(() => {
      setPatientDrugRules(getStateRules(patientInfo?.[id]?.Address?.StateCode?.toUpperCase(), settingsPerState))
    }, [settingsPerState, patientInfo?.[id]?.Address?.StateCode])

    const onSetIsCollapsed = useCallback(() => {
      if (isCollapsed && (!mediaFiles?.length || !hasOpened) && mediaSummary?.Count) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        dispatch(getMediaFiles({ patientId: id, mediaFiles: [] }))
      } else {
        if (!hasOpened) {
          setHasOpened(true)
        }
        setIsCollapsed((prev) => !prev)
      }
    }, [isCollapsed, mediaFiles?.length, hasOpened, mediaSummary?.Count, dispatch, id])

    useImperativeHandle(ref, () => ({
      openFiles() {
        if (!hasOpened) {
          setIsCollapsed(false)
          setHasOpened(true)
        }
      },
      closeFileTags() {
        // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
        fileCardRef.current.onFilesAddedSuccess()
      },
      closeUpload() {
        setShowUploadPopUp(false)
      },
    }))
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    const onChangeFileUpload = (event) => {
      const files = event?.target?.files?.[0]
      const extension = files.name.split('.').pop().toLowerCase()
      const isAllowedExtension = ALLOWED_FILE_EXTENSIONS.includes(extension)
      const isAllowedSize = files.size <= SIZE_10_MB

      if (!isAllowedExtension) {
        dispatch(
          setChartsError(
            getUserFriendlyErrorMessage(errorCodes.INVALID_FILE_FORMAT, null, ALLOWED_FILE_EXTENSIONS_STR),
          ),
        )
      } else if (!isAllowedSize) {
        dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.TOO_BIG_FILE_SIZE, null, '10 MB')))
      }

      event.target.value = null

      if (files && isAllowedExtension && isAllowedSize) {
        setUploadedFile(files)
        setShowUploadPopUp(true)
      }
    }

    const onAddMediaFile = useCallback(
      // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
      (file) => {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        dispatch(addMediaFile(file, mediaFiles, hasOpened))
      },
      [hasOpened, mediaFiles, dispatch],
    )

    const onDeleteMediaFile = useCallback(
      // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
      (fileId) => {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        dispatch(deleteMediaFile(id, fileId))
      },
      [dispatch, id],
    )
    return (
      <>
        {showUploadPopUp ?
          <div className='file-section-upload-pop-up-container'>
            <UploadFilePopUp
              patientId={id}
              file={uploadedFile}
              close={() => {
                setShowUploadPopUp(false)
              }}
              patientDrugRules={patientDrugRules}
              addMediaFile={onAddMediaFile}
              setToastErrorMessage={setToastErrorMessage}
            />
          </div>
        : null}
        {/*
         // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
        <ChartSection
          className={classNames('files-section', { 'extend-height': mediaFiles.length })}
          title={text.FILES}
          icon={filesIcon}
          iconAltText={text.FILES}
          setIsCollapsed={onSetIsCollapsed}
          isCollapsed={isCollapsed}
          emptyText={text.NO_FILES}
          userTimeZone={userTimeZone}
          isWithoutLastUpdated
          forceEmpty={mediaFiles.length ? filteredMediaFiles.length === 0 : false}
          isShowButton={!isMergedAccount}
          customButton={
            <label htmlFor='file-card-file-upload' className='file-card-file-upload'>
              <SecondaryButton
                btnText={
                  <>
                    <span>{text.NEW}&nbsp;</span>
                    <input
                      type='file'
                      id='file-card-file-upload'
                      accept={ALLOWED_FILE_EXTENSIONS_STR}
                      onChange={onChangeFileUpload}
                    />
                  </>
                }
                containerStyle={styles.NEW_BUTTON_STYLE}
                image={plusIcon}
                imageAlt={text.ADD_NEW_FILE}
                imageStyle={styles.NEW_BUTTON_IMAGE_STYLE}
                onPress={() => {}}
              />
            </label>
          }>
          {mediaFiles.length ?
            <>
              {getTagFilter()}
              {filteredMediaFiles?.length ?
                <div className='files-section-container' id='files-section-container'>
                  <FlatList
                    displayGrid
                    minColumnWidth='49%'
                    gridGap='16px'
                    list={filteredMediaFiles || []}
                    renderItem={(file, index) => (
                      <FileCard
                        ref={fileCardRef}
                        // @ts-expect-error TS2698 -- MIGRATION: not approved, fix when encountered
                        {...file}
                        className='patient-chart-file-card-wrapper'
                        // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
                        key={file.FileId}
                        index={index}
                        patientId={id}
                        apiCall={apiCall}
                        // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
                        dateTimeString={parseUserDateTime(file.UploadDateTime, userTimeZone, false)}
                        // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
                        setMediaFileTags={(mediaFile) => {
                          // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
                          dispatch(setMediaFileTags(mediaFile))
                        }}
                        patientDrugRules={patientDrugRules}
                        setToastErrorMessage={setToastErrorMessage}
                        onDelete={onDeleteMediaFile}
                      />
                    )}
                  />
                </div>
              : null}
            </>
          : null}
        </ChartSection>
      </>
    )
  },
)
